import { useState } from "react";
import { Shield, ShieldCheck, ShieldOff } from "lucide-react";
import { cockpit, useCockpitAuth } from "@/lib/cockpitAuth";
import LegalSettingsPanel from "./CockpitLegalSettings";

export default function CockpitSettings() {
    const { user, refresh } = useCockpitAuth();
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");

    const startSetup = async () => {
        setBusy(true);
        setErr("");
        try {
            const { data } = await cockpit.post("/auth/totp/setup");
            setSetup(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur");
        } finally {
            setBusy(false);
        }
    };

    const verify = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await cockpit.post("/auth/totp/verify", { code });
            setSetup(null);
            setCode("");
            setMsg("TOTP activé. La double authentification sera requise à la prochaine connexion.");
            await refresh();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Code invalide");
        } finally {
            setBusy(false);
        }
    };

    const disable = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await cockpit.post("/auth/totp/disable", { code });
            setCode("");
            setMsg("TOTP désactivé.");
            await refresh();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Code invalide");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="cockpit-settings" className="px-5 sm:px-8 lg:px-16 py-10 lg:py-14 max-w-[760px]">
            <div className="overline text-[#d4af37]/70 mb-4">
                Sécurité du compte
            </div>
            <h1
                className="font-serif text-[28px] sm:text-[34px] md:text-[40px] lg:text-[48px] leading-[1.08] text-[#f4ecd8] mb-12"
                style={{ fontWeight: 300 }}
            >
                Authentification.
            </h1>

            <section className="border-t border-white/[0.08] pt-10">
                <div className="flex items-start gap-5">
                    {user.totp_enabled ? (
                        <ShieldCheck strokeWidth={1} size={28} className="text-[#d4af37]" />
                    ) : (
                        <ShieldOff strokeWidth={1} size={28} className="text-[#f4ecd8]/40" />
                    )}
                    <div className="flex-1">
                        <h2
                            className="font-serif text-[22px] text-[#f4ecd8] mb-3"
                            style={{ fontWeight: 400 }}
                        >
                            Double authentification (TOTP)
                        </h2>
                        <p className="text-[14px] text-[#f4ecd8]/65 leading-[1.85] font-light mb-8">
                            {user.totp_enabled
                                ? "La double authentification est activée. Un code à 6 chiffres généré par votre application (Google Authenticator, Authy, 1Password…) vous sera demandé à chaque connexion."
                                : "La double authentification ajoute une seconde couche de sécurité au compte. Vivement recommandée."}
                        </p>

                        {msg && (
                            <div className="text-[13px] text-[#d4af37] border-l-2 border-[#d4af37] pl-4 py-1 mb-6">
                                {msg}
                            </div>
                        )}
                        {err && (
                            <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1 mb-6">
                                {err}
                            </div>
                        )}

                        {!user.totp_enabled && !setup && (
                            <button
                                type="button"
                                data-testid="totp-setup-start"
                                onClick={startSetup}
                                disabled={busy}
                                className="inline-flex items-center gap-3 px-6 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                            >
                                <Shield strokeWidth={1.2} size={14} />
                                Activer le TOTP
                            </button>
                        )}

                        {setup && (
                            <div className="mt-4 p-8 border border-white/[0.08] bg-white/[0.02]">
                                <p className="text-[13px] text-[#f4ecd8]/70 mb-6">
                                    Scannez ce QR code dans votre application d'authentification :
                                </p>
                                <div className="flex flex-col sm:flex-row gap-8 items-start">
                                    <img
                                        src={`data:image/png;base64,${setup.qr_png_base64}`}
                                        alt="QR TOTP"
                                        className="w-44 h-44 bg-white p-3"
                                    />
                                    <div className="flex-1">
                                        <div className="overline text-[#d4af37]/80 mb-2">Clé manuelle</div>
                                        <code className="block text-[12px] text-[#f4ecd8]/75 break-all mb-6 font-mono">
                                            {setup.secret}
                                        </code>
                                        <form onSubmit={verify}>
                                            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                                Code à 6 chiffres
                                            </label>
                                            <input
                                                data-testid="totp-verify-code"
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]{6}"
                                                maxLength={6}
                                                required
                                                value={code}
                                                onChange={(e) =>
                                                    setCode(e.target.value.replace(/\D/g, ""))
                                                }
                                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[20px] font-serif tracking-[0.5em] text-[#f4ecd8]"
                                                placeholder="000000"
                                                autoFocus
                                            />
                                            <button
                                                type="submit"
                                                data-testid="totp-verify-submit"
                                                disabled={busy}
                                                className="mt-5 px-6 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                                            >
                                                {busy ? "Vérification…" : "Confirmer"}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}

                        {user.totp_enabled && (
                            <form onSubmit={disable} className="mt-6 flex items-end gap-4">
                                <div className="flex-1">
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                        Code actuel pour désactiver
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]{6}"
                                        maxLength={6}
                                        required
                                        value={code}
                                        onChange={(e) =>
                                            setCode(e.target.value.replace(/\D/g, ""))
                                        }
                                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2.5 text-[16px] font-serif tracking-[0.4em] text-[#f4ecd8]"
                                        placeholder="000000"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="px-5 py-2.5 border border-[#e89090]/40 text-[#e89090]/80 text-[11px] tracking-[0.3em] uppercase hover:text-[#e89090] hover:border-[#e89090] transition-all duration-500 disabled:opacity-40"
                                >
                                    Désactiver
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* LEGAL / MENTIONS LÉGALES */}
            <LegalSettingsPanel />
        </div>
    );
}
